import { useState } from "react";
import { Copy, Check } from "lucide-react";

/**
 * Renders a wallet address with copy button and a simple QR-like visual.
 * Uses a canvas-free SVG approach for the QR placeholder —
 * will be replaced with a real QR lib (e.g. qrcode.react) later.
 */
interface WalletQRProps {
  address: string;
  size?: number;
}

function WalletQR({ address, size = 160 }: WalletQRProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Generate a deterministic grid pattern from the address as a visual placeholder
  const cells = 9;
  const cellSize = size / cells;
  const hexChars = Array.from(address.replace("0x", ""));
  const bytes = hexChars.map((c) => parseInt(c, 16) || 0);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="rounded-xl border border-border-subtle bg-white p-3"
        style={{ width: size + 24, height: size + 24 }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {Array.from({ length: cells * cells }).map((_, i) => {
            const x = (i % cells) * cellSize;
            const y = Math.floor(i / cells) * cellSize;
            const byteIdx = i % bytes.length;
            const filled = (bytes[byteIdx] ?? 0) > 7;
            return (
              <rect
                key={i}
                x={x}
                y={y}
                width={cellSize - 1}
                height={cellSize - 1}
                rx={2}
                fill={filled ? "#1a1a2e" : "#f0f0f5"}
              />
            );
          })}
        </svg>
      </div>

      <button
        type="button"
        onClick={handleCopy}
        className="flex items-center gap-2 rounded-lg bg-surface-secondary px-3 py-2 transition-colors hover:bg-surface-tertiary"
      >
        <span className="max-w-[200px] truncate font-mono text-xs text-text-secondary">
          {address}
        </span>
        {copied ? (
          <Check className="h-4 w-4 shrink-0 text-success-500" />
        ) : (
          <Copy className="h-4 w-4 shrink-0 text-text-muted" />
        )}
      </button>

      {copied && (
        <span className="text-xs text-success-500">Copied!</span>
      )}
    </div>
  );
}

export { WalletQR };
